import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, DollarSign, CreditCard, Flag, AlertCircle, Download, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const BillingTab = ({ tenant }) => {
  const [gumroadStatus, setGumroadStatus] = useState(null);
  const [creditLog, setCreditLog] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    if (tenant?.id) fetchAll();
  }, [tenant?.id]);

  useEffect(() => {
    if (tenant?.id) fetchCreditLog();
  }, [tenant?.id, selectedMonth]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statusRes, revenueRes] = await Promise.all([
        api.get(`/billing/tenant-status/${tenant.id}`),
        api.get(`/billing/tenant-revenue/${tenant.id}`),
      ]);
      setGumroadStatus(statusRes.data);
      setRevenue(revenueRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchCreditLog = async () => {
    try {
      const res = await api.get(`/billing/credit-log/${tenant.id}?month=${selectedMonth}`);
      setCreditLog(res.data || []);
    } catch { setCreditLog([]); }
  };

  const exportCreditLog = () => {
    const headers = ['Date', 'Horse', 'Sale', 'Opportunity', 'User'];
    const rows = creditLog.map(e => [
      e.created_at ? new Date(e.created_at).toLocaleDateString() : '',
      e.horse_name || '', e.sale_name || '', e.opportunity_name || '', e.user_name || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `credit-usage-${tenant.name}-${selectedMonth}.csv`;
    a.click();
  };

  // Generate month options (last 12 months)
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const billing = gumroadStatus?.billing || {};

  return (
    <div className="space-y-6">
      <Tabs defaultValue="usage">
        <TabsList>
          <TabsTrigger value="usage">Usage & Billing</TabsTrigger>
          <TabsTrigger value="features">Feature Flags</TabsTrigger>
        </TabsList>

        <TabsContent value="usage" className="space-y-4">
          {/* Subscription Status — Top */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Subscription Status</CardTitle>
                {!gumroadStatus?.subscription_active && (
                  <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                    onClick={async () => {
                      const cycle = window.prompt('Billing cycle (monthly / yearly):', 'monthly');
                      if (!cycle) return;
                      try {
                        await api.post(`/billing/mark-subscription-active/${tenant.id}`, { billing_cycle: cycle });
                        toast.success('Subscription marked active');
                        fetchAll();
                      } catch { toast.error('Failed to update'); }
                    }}>
                    Mark Active
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Overall:</span>
                <Badge className={gumroadStatus?.subscription_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {gumroadStatus?.subscription_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Recurrence</th>
                    <th className="text-left px-3 py-2">Action</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(billing).filter(([k]) => k !== 'status').map(([key, val]) => (
                      <tr key={key} className="border-t">
                        <td className="px-3 py-2 font-medium capitalize">{key.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2">
                          {val?.status === 'pending_activation' ? (
                            <Badge className={key === 'setup_assistance' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}>
                              {key === 'setup_assistance' ? 'Requested' : 'Pending Activation'}
                            </Badge>
                          ) : (
                            <Badge className={val?.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                              {val?.active ? 'Active' : val?.paid ? 'Paid' : 'Inactive'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 capitalize">{val?.recurrence || val?.quantity ? `${val.quantity} slots` : '—'}</td>
                        <td className="px-3 py-2">
                          {val?.status === 'pending_activation' ? (
                            <Button size="sm" className="h-6 text-xs bg-primary hover:bg-primary/90 text-white" onClick={async () => {
                              try {
                                await api.post(`/billing/approve-feature/${tenant.id}/${key}`);
                                toast.success(key === 'setup_assistance' ? 'Setup assistance marked as reviewed' : `${key.replace(/_/g, ' ')} activated`);
                                fetchAll();
                              } catch { toast.error('Failed to activate'); }
                            }}>{key === 'setup_assistance' ? 'Confirm' : 'Activate'}</Button>
                          ) : val?.started_at ? new Date(val.started_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Summary */}
          {revenue && (
            <Card>
              <CardHeader><CardTitle className="text-base">Revenue Summary</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">YTD Revenue</p>
                    <p className="text-2xl font-bold">${(revenue.ytd / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">All-Time Revenue</p>
                    <p className="text-2xl font-bold">${(revenue.all_time / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2 italic">Approximate — based on webhook events. Does not reflect Gumroad processing fees or refunds.</p>
              </CardContent>
            </Card>
          )}

          {/* Sale Entry Credits + Usage Log */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Ticket className="w-4 h-4" /> Sale Entry Credits</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Remaining</p>
                    <span className="text-2xl font-bold">{gumroadStatus?.sale_entry_credits ?? 0}</span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Used This Month</p>
                    <span className="text-2xl font-bold">{gumroadStatus?.credits_used_this_month ?? 0}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Purchased: {gumroadStatus?.total_credits_purchased ?? 0} | Used: {gumroadStatus?.total_credits_used ?? 0}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  Credit Usage Log
                  <div className="flex items-center gap-2">
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="h-7 text-xs w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="h-7" onClick={exportCreditLog} title="Export CSV">
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {creditLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No credits used this month.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0"><tr>
                        <th className="text-left px-2 py-1">Date</th>
                        <th className="text-left px-2 py-1">Horse</th>
                        <th className="text-left px-2 py-1">Sale</th>
                        <th className="text-left px-2 py-1">Type</th>
                      </tr></thead>
                      <tbody>
                        {creditLog.map((e, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1">{e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</td>
                            <td className="px-2 py-1">{e.horse_name || '—'}</td>
                            <td className="px-2 py-1">{e.sale_name || '—'}</td>
                            <td className="px-2 py-1">
                              {e.is_demo
                                ? <span className="text-blue-600 font-medium">Demo</span>
                                : <span className="text-green-700">Billed</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="features">
          <Card>
            <CardHeader><CardTitle className="text-base">Feature Flags</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(gumroadStatus?.features || {}).length > 0 ? (
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(gumroadStatus?.features || {}).map(([key, enabled]) => (
                    <Badge key={key} className={enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                      {key.replace(/_/g, ' ')}: {enabled ? 'ON' : 'OFF'}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No premium features enabled</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingTab;
